import modalStyle from "./modal.module.scss";

import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckFillIcon } from "assets/images/icons/checkbox_fiilled.svg";
import btn_delete from 'assets/images/icons/icn_table_cell_Unverified.svg';
import btn_add from "assets/images/icons/btn_add.svg";

import React, { useState, useEffect, memo } from 'react';
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Checkbox, Select, MenuItem, TextField } from "@mui/material";
import { cloneDeep } from 'lodash';

import { isValidIpV4, compareStartAndEndIp } from "utils/network-validation";
import { getDefaultTextFieldProp, getDefaultSelectProp } from "../theme";
import { defaultLayer3Type, defaultLayer3Setting } from "../const";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import IpInputField from "components/InputField/IpInputField";

const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;

const ModalSettings = ({
  title,
  closeOpenModal,
  proxyDevices,
  selectedProxyDevice,
  onSelectProxyDevice,
  layerSetting,
  setLayerSetting,
  renderCount,
  selectProxyDeviceCallback,
  notSelectProxyDeviceCallback,
}) => {
  const theme = useTheme();
  const isCloudVersion = checkEnvironment === 'cloud' ? true : false;

  // State
  const [isRendered, setIsRendered] = useState(false);
  const [proxyDevicesItems, setProxyDevicesItems] = useState([]);
  const [isLayer2, setIsLayer2] = useState(true);
  const [isLayer3, setIsLayer3] = useState(false);
  const [layer3Settings, setLayer3Settings] = useState([
    {
      ...defaultLayer3Setting, 'type': defaultLayer3Type.select,
    }
  ]);

  const menuCheckStyle = {
    "height": "20px",
    "&:hover": {
      backgroundColor: 'transparent !important'
    },
  };

  // Method
  const closeModal = () => {
    closeOpenModal();
    if (isCloudVersion && renderCount === 0) {
      notSelectProxyDeviceCallback();
    }
  }

  const changeStartIp = (value, index) => {
    const config = layer3Settings[index];
    if (config.type.value === defaultLayer3Type.select.value) {
      return;
    }

    const updatedLayer3Settings = [...layer3Settings];
    updatedLayer3Settings[index].startIp = value;
    updatedLayer3Settings[index].startIpErr = !isValidIpV4(value);
    setLayer3Settings(updatedLayer3Settings);
  };

  const changeEndIp = (value, index) => {
    const config = layer3Settings[index];
    if (config.type.value === defaultLayer3Type.select.value) {
      return;
    }

    const updatedLayer3Settings = [...layer3Settings];
    updatedLayer3Settings[index].endIp = value;
    updatedLayer3Settings[index].endIpErr = !isValidIpV4(value);
    setLayer3Settings(updatedLayer3Settings);
  };

  const changeRange = (e, index) => {
    const config = layer3Settings[index];
    if (config.type.value === defaultLayer3Type.select.value) {
      return;
    }

    const updatedLayer3Settings = [...layer3Settings];
    updatedLayer3Settings[index].range = e.target.value;

    if (e.target.value.length > 0) {
      const rangeValue = parseInt(e.target.value, 10);
      if (isNaN(rangeValue) || rangeValue < 0 || rangeValue > 32) {
        updatedLayer3Settings[index].rangeErr = true;
      } else {
        updatedLayer3Settings[index].rangeErr = false;
      }
    }

    setLayer3Settings(updatedLayer3Settings);
  };

  const checkLayer3Setting = () => {
    let isValidResults = [];

    if (!isLayer2 && !isLayer3) {
      return false;
    }

    if (!isLayer3) {
      return true;
    }

    layer3Settings.forEach((layer3Setting) => {
      if (layer3Setting.type.value === defaultLayer3Type.select.value) {
        isValidResults.push(false);
        return;
      }

      if (layer3Setting.type.value === defaultLayer3Type.ip.value) {
        if (layer3Setting.startIp.length > 0 && layer3Setting.endIp.length > 0) {
          if (layer3Setting.startIpErr || layer3Setting.endIpErr) {
            isValidResults.push(false);
          }
          if (!compareStartAndEndIp(layer3Setting.startIp, layer3Setting.endIp)) {
            isValidResults.push(false);
          }
        } else {
          isValidResults.push(false);
        }
      } else if (layer3Setting.type.value === defaultLayer3Type.subnet.value) {
        if (layer3Setting.startIp.length > 0 && layer3Setting.range.length > 0) {
          if (layer3Setting.startIpErr || layer3Setting.rangeErr) {
            isValidResults.push(false);
          }
          if (layer3Setting.range < 0 || layer3Setting.range > 32) {
            isValidResults.push(false);
          }
        } else {
          isValidResults.push(false);
        }
      }
    });

    return isValidResults.every((result) => result === true);
  };

  const checkForm = () => {
    if (isCloudVersion) {
      if (!selectedProxyDevice) {
        return false;
      }
    }

    return checkLayer3Setting();
  }

  const updateLayerSetting = () => {
    const updatedLayerSetting = {
      layer2: {
        value: isLayer2,
      },
      layer3: {
        value: isLayer3,
        configures: [],
      },
    };

    layer3Settings.forEach((layer3Setting) => {
      const updatedLayer3Setting = {
        type: layer3Setting.type,
        startIp: layer3Setting.startIp,
        startIpErr: layer3Setting.startIpErr,
        endIp: layer3Setting.endIp,
        endIpErr: layer3Setting.endIpErr,
        range: layer3Setting.range,
        rangeErr: layer3Setting.rangeErr,
      };

      updatedLayerSetting.layer3.configures.push(updatedLayer3Setting);
    });
    setLayerSetting(updatedLayerSetting);
  };

  // Side effect
  useEffect(() => {
    const items = proxyDevices.map((device) => ({
      value: device.proxyUuid,
      label: `${device.deviceName}`,
      linkStatus: device.linkStatus,
    }));
    setProxyDevicesItems(items);
  }, [proxyDevices, selectedProxyDevice]);

  useEffect(() => {
    if (layerSetting.layer2) {
      setIsLayer2(layerSetting.layer2.value);
    } else {
      setIsLayer2(false);
    }

    if (layerSetting.layer3) {
      setIsLayer3(layerSetting.layer3.value);
    } else {
      setIsLayer3(false);
    }

    const updatedLayer3Settings = [];
    if (layerSetting.layer3.configures) {
      layerSetting.layer3.configures.forEach((layer3Setting) => {
        updatedLayer3Settings.push(cloneDeep(layer3Setting));
      });
    } else {
      updatedLayer3Settings.push({
        ...defaultLayer3Setting,
        'type': defaultLayer3Type.select,
      });
    }

    setLayer3Settings(updatedLayer3Settings);
    setIsRendered(true);
  }, [layerSetting.layer2, layerSetting.layer3]);

  if (!isRendered) {
    return null;
  }

  return (
    <>
      <div className={modalStyle['modal_Block']} style={{ margin: '20px 0' }}>
        <Typography color="#707ca8" fontWeight="600" fontSize="20px">
          {title}
        </Typography>
      </div>
      {
        isCloudVersion && (
          <>
            <div className={modalStyle['proxy-device-selection']}>
              <div className={modalStyle['title']}>Proxy Device</div>
              <Select
                style={{ ...getDefaultSelectProp().style, width: '99%' }}
                value={proxyDevicesItems.find(item => item.value === (selectedProxyDevice ? selectedProxyDevice.proxyUuid : '')) ? (selectedProxyDevice ? selectedProxyDevice.proxyUuid : '') : ''}
                onChange={(e) => {
                  const selectedDevice = proxyDevices.find(
                    (device) => device.proxyUuid === e.target.value
                  );
                  onSelectProxyDevice(selectedDevice);
                }}
                sx={{ ...getDefaultSelectProp().sx }}
                MenuProps={{ ...getDefaultSelectProp().menuProps }}
              >
                {
                  proxyDevicesItems.map((item, index) => (
                    <MenuItem
                      value={item.value} key={index}
                      style={{
                        color: item.linkStatus === 'online' ? theme.palette.primary.main : theme.palette.primary.greyThread,
                        pointerEvents: item.linkStatus === 'online' ? 'auto' : 'none',
                        cursor: item.linkStatus === 'online' ? 'default' : 'not-allowed',
                        marginBottom: '4px',
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))
                }
              </Select>
            </div>
          </>
        )
      }
      <div style={{ margin: '20px 0' }}>
        <div className={modalStyle['discover-settings']}>
          <div className={modalStyle['type']}>
            <Checkbox
              className={modalStyle['checkbox']}
              icon={<CheckDefIcon className={modalStyle['checkboxIcon']} />}
              checkedIcon={<CheckFillIcon className={modalStyle['checkboxIcon']} />}
              checked={isLayer2}
              onChange={() => setIsLayer2(!isLayer2)}
              sx={menuCheckStyle}
            />
            <div className={modalStyle['title']}>Layer 2</div>
          </div>
          <div className={modalStyle['type']}>
            <Checkbox
              className={modalStyle['checkbox']}
              icon={<CheckDefIcon className={modalStyle['checkboxIcon']} />}
              checkedIcon={<CheckFillIcon className={modalStyle['checkboxIcon']} />}
              checked={isLayer3}
              onChange={() => setIsLayer3(!isLayer3)}
              sx={menuCheckStyle}
            />
            <div className={modalStyle['title']}>Layer 3(IP)</div>
          </div>
          <div className={modalStyle['layer3']}>
            <Box display={'flex'} flexDirection={'column'} style={{ width: '100%' }}>
              <Box></Box>
              {
                layer3Settings.map((layer3Setting, index) => (
                  <div key={index} className={modalStyle['item']}>
                    <Box className={modalStyle['container']} style={{ paddingTop: '6px' }}>
                      <Select
                        style={{ ...getDefaultSelectProp().style }}
                        value={layer3Setting.type.value}
                        onChange={(e) => {
                          const updatedLayer3Settings = cloneDeep(layer3Settings);
                          updatedLayer3Settings[index].type = defaultLayer3Type[e.target.value];
                          setLayer3Settings(updatedLayer3Settings);
                        }}
                        sx={{ ...getDefaultSelectProp().sx }}
                        MenuProps={{ ...getDefaultSelectProp().menuProps }}
                      >
                        {Object.values(defaultLayer3Type).map((type) => (
                          <MenuItem
                            value={type.value}
                            key={`${index}-${type.value}`}
                          >
                            {type.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    {
                      layer3Setting.type.value === defaultLayer3Type.select.value && (
                        <>
                          <Box className={modalStyle['container']}>
                            <IpInputField
                              value={layer3Setting.startIp}
                              onChange={(e) => { changeStartIp(e, index); }}
                              disabled={true}
                            />
                          </Box>
                          <Box className={modalStyle['container']} style={{ paddingTop: '10px' }}>
                            <div className={modalStyle['center']}>-</div>
                          </Box>
                          <Box
                            className={modalStyle['container']}
                            style={{ gridColumn: index === 0 ? '4/6' : '' }}
                          >
                            <IpInputField
                              value={layer3Setting.endIp}
                              onChange={value => changeEndIp(value, index)}
                              disabled={true}
                            />
                          </Box>
                        </>
                      )
                    }
                    {
                      layer3Setting.type.value !== defaultLayer3Type.select.value && (
                        <>
                          <Box className={modalStyle['container']}>
                            <IpInputField
                              value={layer3Setting.startIp}
                              onChange={value => changeStartIp(value, index)}
                              disabled={layer3Setting.type.value === defaultLayer3Type.select.value}
                            />
                            {layer3Setting.startIpErr && (
                              <div className={modalStyle['error']}>
                                Invalid IP address
                              </div>
                            )}
                          </Box>
                        </>
                      )
                    }
                    {
                      layer3Setting.type.value === defaultLayer3Type.ip.value && (
                        <>
                          <Box className={modalStyle['container']} style={{ paddingTop: '10px' }}>
                            <div className={modalStyle['center']}>-</div>
                          </Box>
                          <Box
                            className={modalStyle['container']}
                            style={{ gridColumn: index === 0 ? '4/6' : '' }}
                          >
                            <IpInputField
                              value={layer3Setting.endIp}
                              onChange={value => changeEndIp(value, index)}
                              disabled={layer3Setting.type.value === defaultLayer3Type.select.value}
                            />
                            {layer3Setting.endIpErr && (
                              <div className={modalStyle['error']}>
                                Invalid IP address
                              </div>
                            )}
                            {layer3Setting.startIp.length > 0 && layer3Setting.endIp.length > 0 &&
                              !layer3Setting.startIpErr && !layer3Setting.endIpErr && !compareStartAndEndIp(layer3Setting.startIp, layer3Setting.endIp) && (
                                <>
                                  <div className={modalStyle['error']}>
                                    Must be greater than or equal to the start IP address
                                  </div>
                                </>
                              )}
                          </Box>
                        </>
                      )
                    }
                    {
                      layer3Setting.type.value === defaultLayer3Type.subnet.value && (
                        <>
                          <Box
                            className={modalStyle['container']}
                            style={{ paddingTop: '10px' }}
                          >
                            <div className={modalStyle['center']}>/</div>
                          </Box>
                          <Box
                            className={modalStyle['container']}
                            style={{ gridColumn: index === 0 ? '4/6' : '', padding: '6px 0 0 6px' }}
                          >
                            <TextField
                              value={layer3Setting.range}
                              size="small"
                              onChange={(e) => { changeRange(e, index); }}
                              variant="outlined"
                              error={layer3Setting.rangeErr}
                              disabled={layer3Setting.type.value === defaultLayer3Type.select.value}
                              sx={{ ...getDefaultTextFieldProp().sx }}
                              inputProps={{ ...getDefaultTextFieldProp().inputProps }}
                            />
                          </Box>
                        </>
                      )
                    }
                    {
                      index !== 0 && (
                        <Box className={modalStyle['container']} style={{ paddingTop: '14px' }}>
                          <div
                            className={modalStyle['delete']}
                            onClick={() => {
                              const updatedLayer3Settings = [...layer3Settings];
                              updatedLayer3Settings.splice(index, 1);
                              setLayer3Settings(updatedLayer3Settings);
                            }}>
                            <img src={btn_delete} alt="delete" />
                          </div>
                        </Box>
                      )
                    }
                  </div>
                ))
              }
            </Box>
          </div>
          <div className={modalStyle['btn-add-container']}>
            <div
              className={modalStyle['btn']}
              onClick={() => {
                const updatedLayer3Settings = [...layer3Settings];
                updatedLayer3Settings.push({
                  ...defaultLayer3Setting,
                  'type': defaultLayer3Type.select,
                });
                setLayer3Settings(updatedLayer3Settings);
              }}
            >
              <img
                src={btn_add}
                alt="Add"
              />
              <span>Add</span>
            </div>
          </div>
        </div>
      </div >
      <div className={modalStyle['modal_operation']}>
        <OutlineButton
          className={modalStyle['modal_cancelBtn']}
          style={{ width: '195px', height: '42px', fontSize: '16px' }}
          variant="contained"
          onClick={closeModal}
        >
          Cancel
        </OutlineButton>
        <SolidButton
          className={modalStyle['modal_saveBtn']}
          style={{ width: '195px', height: '42px', fontSize: '16px' }}
          variant="contained"
          disabled={!checkForm()}
          onClick={() => {
            updateLayerSetting();
            closeOpenModal();
            selectProxyDeviceCallback();
          }}
        >
          {
            (isCloudVersion && renderCount === 0) ? 'Next' : 'Save'
          }
        </SolidButton>
      </div>
    </>
  );
}

export default memo(ModalSettings);